import { CompanyProfileType, InvestorProfileType } from '@/api/backend/types';
import { PageLoading } from '@/components/page-loading';
import { userProfileDetailsQueryOptions } from '@/queryOptions';
import { useAuthStore } from '@/store/auth';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { createContext, useEffect } from 'react';

type ProfileContextType = {
  profile: InvestorProfileType | CompanyProfileType;
};

// eslint-disable-next-line react-refresh/only-export-components
export const ProfileContext = createContext<ProfileContextType>({
  profile: {} as InvestorProfileType | CompanyProfileType,
});

export const Route = createFileRoute('/_auth/profile')({
  component: RootComponent,
  context: () => {
    return {
      getBreadcrumbData: () => ({
        id: null,
        translationKey: null,
      }),
    };
  },
  head: () => {
    return {
      meta: [
        {
          title: 'Profile - OPAZ E-Service Portal',
        },
      ],
    };
  },
});

export function RootComponent() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { activeProfile } = useAuthStore((state) => state);
  const { data: profile, isLoading } = useQuery(userProfileDetailsQueryOptions(activeProfile.id));

  const isProfileBasePath = pathname === '/profile' || pathname === '/profile/';

  useEffect(() => {
    if (!profile || !isProfileBasePath) return;

    if (profile.type === 'INVESTOR') {
      navigate({ to: '/profile/investor', replace: true });
    } else {
      navigate({ to: '/profile/company', replace: true });
    }
  }, [isProfileBasePath, navigate, profile]);

  if (isLoading || !profile || isProfileBasePath) {
    return <PageLoading />;
  }

  return (
    <ProfileContext.Provider value={{ profile }}>
      <Outlet />
    </ProfileContext.Provider>
  );
}
